import {HttpErrorResponse, HttpInterceptorFn} from '@angular/common/http';
import {inject} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {forkJoin, throwError} from 'rxjs';
import {catchError, take} from 'rxjs/operators';
import {RouterService} from '../../global/router.service';
import {LocalStorageKeys, StorageService} from '../../global/storage.service';
import {EditorEmitterService} from '../../main/pages/events/editor-emitter.service';
import {AuthFacade} from '../../store/auth.facade';
import {TranslocoService} from '@jsverse/transloco';
import { StatusCodes } from './status-codes';

export const apiAuthFallbackInterceptor: HttpInterceptorFn = (request, next) => {
    const editorEmitter = inject(EditorEmitterService) ;
    const modalService = inject(NgbModal);
    const storageService = inject(StorageService);
    const authFacade = inject(AuthFacade);
    const routerService = inject(RouterService);
    const translocoService = inject(TranslocoService, { optional: true });

    return next(request.clone())
        .pipe(catchError((err) => {
            if ((err?.status === 401 || err?.message == 'Unauthenticated.') || err?.status === 419) {
                editorEmitter.setUnsavedChangesFlag(false);
                modalService.dismissAll(); // If there has active modals then i closed it on logout
                storageService.removeItems([LocalStorageKeys.USER]);
                forkJoin([
                    authFacade.isThirdPartyLogin$.pipe(take(1)),
                    authFacade.isIframeAbleLogin$.pipe(take(1))
                  ]).subscribe(([isThirdPartyLogin, isIframeAbleLogin]) => {
                    const url = isThirdPartyLogin
                      ? '/crm-login'
                      : isIframeAbleLogin
                      ? 'iframeable/login'
                      : '/login';
                  
                    routerService.redirectCrmUserToRoute(url);
                    authFacade.clearState();
                  });
            }


                return throwError(apiAuthFallbackMessageProvider(err, translocoService));

        }));
}

// Laravel's default body for a `can:` middleware / policy denial. Anything
// else in a 403 body is a message the backend wrote on purpose, so we keep it.
const LARAVEL_DEFAULT_DENIAL = 'This action is unauthorized.';

const PERMISSION_DENIED_KEYS: { urlFragment: string, key: string }[] = [
    {
        urlFragment: '/documenttypes',
        key: 'Permission denied toast for document type settings (Settings permissions) - You do not have permission to change document type settings. Your role needs the Settings > Document Type permission. Ask your workspace administrator to add it.'
    },
    {
        urlFragment: '/categories',
        key: 'Permission denied toast for categories (Settings permissions) - You do not have permission to manage categories. Your role needs the Settings > Category permission. Ask your workspace administrator to add it.'
    },
];

const PERMISSION_DENIED_GENERIC_KEY = 'Permission denied toast generic (Settings permissions) - You do not have permission to perform this action. Ask your workspace administrator to update your role.';

/**
 * Turns a bare 403 from the backend's permission middleware into a message the
 * user can act on. Returns null when the 403 carries a custom backend message,
 * so that message is shown instead.
 */
export const permissionDeniedMessageProvider = (error: HttpErrorResponse, translocoService?: TranslocoService | null): string | null => {
    if (error?.status !== StatusCodes.FORBIDDEN || !translocoService) {
        return null;
    }

    const backendMessage = error?.error?.message;
    if (backendMessage && backendMessage !== LARAVEL_DEFAULT_DENIAL) {
        return null;
    }

    const path = (error?.url ?? '').split('?')[0];
    const match = PERMISSION_DENIED_KEYS.find(entry => path.includes(entry.urlFragment));
    return translocoService.translate(match ? match.key : PERMISSION_DENIED_GENERIC_KEY);
}

export const apiAuthFallbackMessageProvider = (error:HttpErrorResponse, translocoService?: TranslocoService | null) => {
    if (error?.status === StatusCodes.INSUFFICIENT_SPACE_ON_RESOURCE) {
        return error;
    }

    if(error?.status === StatusCodes.NOT_FOUND){
        return error
    }

    const temporaryPath = ['templates', 'documents', 'fieldsdatas', 'contractcontrols', 'documents-search'];
    if (error?.status === StatusCodes.UNPROCESSABLE_ENTITY && temporaryPath.some(path => error?.url?.includes(path))) {
        return error;
    }

    if(Object.prototype.hasOwnProperty.call(error?.error ?? {}, 'error') && Object.prototype.hasOwnProperty.call(error?.error?.error ?? {}, 'code')){
        return error?.error;
    }

    if(error?.status === StatusCodes.UNAUTHORIZED || error?.status === StatusCodes.TOO_MANY_REQUESTS){
        return error;
    }

    const permissionDeniedMessage = permissionDeniedMessageProvider(error, translocoService);
    if (permissionDeniedMessage) {
        return permissionDeniedMessage;
    }

    return error?.error?.message || error?.message || '';
}
